<template>
  <div class="p-5">
    <h1 v-if="isCopyMode" class="h2">Kopier leieobjekt fra {{ rentalObject.street }}</h1>
    <h1 v-else class="h2">Nytt leieobjekt</h1>

    <p v-if="isCopyMode">
      Når du kopierer et leieobjekt bør du redigere data og/eller gi leieobjektet et unikt alias, for å skille det nye
      fra det opprinnelige.
    </p>

    <BlockContainer class="p-5 mt-5">
      <ValidationObserver ref="newRentalObjectForm">
        <form @submit.prevent="onSubmit">
          <h2 class="h5">Hva leier du ut?</h2>

          <div class="row mt-5 text-center">
            <div class="col">
              <hl-RadioButtonTapWithIcon
                iconClass="budicon-apartment-a"
                v-model="rentalObject.type"
                radioValue="apartment"
                layout="vertical"
              >
                <p>Leilighet</p>
              </hl-RadioButtonTapWithIcon>
            </div>
            <div class="col">
              <hl-RadioButtonTapWithIcon
                iconClass="budicon-house-a"
                v-model="rentalObject.type"
                radioValue="house"
                layout="vertical"
              >
                <p>Enebolig</p>
              </hl-RadioButtonTapWithIcon>
            </div>
            <div class="col">
              <hl-RadioButtonTapWithIcon
                iconClass="budicon-monitor-resolution"
                v-model="rentalObject.type"
                radioValue="dorm"
                layout="vertical"
              >
                <p>Hybel</p>
              </hl-RadioButtonTapWithIcon>
            </div>
            <div class="col">
              <hl-RadioButtonTapWithIcon
                iconClass="budicon-meatball-ui"
                v-model="rentalObject.type"
                layout="vertical"
                :radioValue="otherValue"
                @input="otherValue = $event"
              >
                <p>Andre</p>
              </hl-RadioButtonTapWithIcon>
            </div>
          </div>

          <div v-if="showOtherCategories" class="my-4">
            <ValidationProvider rules="excluded:other" name="Andre leieobjekter" v-slot="{ errors }">
              <hl-Select
                :options="propertyTypeOptions"
                name="Andre leieobjekter"
                placeholder="Velg annen type leieobjekt.."
                placeholderValue="other"
                v-model="rentalObject.type"
                :error="errors[0]"
                @input="otherValue = $event"
              />
            </ValidationProvider>
          </div>

          <div>
            <div class="my-4 pt-4">
              <ValidationProvider rules="required" name="Adresse" v-slot="{ errors }">
                <hl-Input
                  type="text"
                  placeholder="Adresse"
                  label="Adresse"
                  v-model="rentalObject.street"
                  name="Adresse"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div class="row my-4">
              <div class="col">
                <ValidationProvider
                  ref="postalZipcode"
                  name="Postnummer"
                  rules="required|numeric|min:4|max:4"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    type="number"
                    placeholder="0481"
                    label="Postnummer"
                    v-model="rentalObject.postal_zipcode"
                    name="Postnummer"
                    @input="fetchPostalArea(arguments[0])"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>

              <div class="col">
                <ValidationProvider
                  ref="postalArea"
                  name="Poststed"
                  :rules="{
                    required: true,
                  }"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    type="text"
                    placeholder=""
                    label="Poststed"
                    v-model="rentalObject.postal_area"
                    name="Poststed"
                    disabled
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </div>

            <div class="row my-4">
              <div class="col">
                <hl-Input
                  type="text"
                  v-model="rentalObject.apartmentNumber"
                  label="Leilighetsnummer"
                  placeholder="H0302"
                  name="Leilighetsnummer"
                  noValidation
                  @blur="updateFloor"
                />
              </div>

              <div v-if="showRoomNumber" class="col">
                <hl-Input
                  v-model="rentalObject.roomNumber"
                  label="Romnummer"
                  placeholder="3"
                  name="Romnummer"
                  type="number"
                />
              </div>

              <div v-if="showFloor" class="col">
                <hl-Input v-model="rentalObject.floor" label="Etasje" placeholder="2" name="Etasje" type="text" />
              </div>
            </div>

            <div class="row my-4">
              <div v-if="isLandlordProfessional" class="col-12 col-md-6">
                <hl-Input v-model="rentalObject.buildingID" label="ByggID" name="ByggID" type="text" />
              </div>
              <div class="col-12 col-md-6">
                <hl-Input v-model="rentalObject.alias" label="Alias / Kallenavn" name="Alias / Kallenavn" type="text" />
              </div>
            </div>

            <!--  <div class="my-4 pt-5">
                <PictureUpload />
              </div> -->
          </div>

          <div class="mt-5 pt-4 text-center">
            <hl-Button variant="secondary" class="mx-2" outline @click="goBack">Avbryt</hl-Button>
            <hl-Button type="submit" class="mx-2" variant="primary">Lagre</hl-Button>
          </div>
        </form>
      </ValidationObserver>
    </BlockContainer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer';
import postalCodeLookup from '@/lib/postalCodeLookup';
import { RentalObjectApi } from '@modules/husleieapi2';
import { rentalObjectCategoryMap as categoryMap } from '@/lib/rentalObjectHelpers';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import RadioButtonTapWithIcon from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButtonTapWithIcon.vue';
import axios from 'axios';

export default {
  components: {
    BlockContainer,
    // PictureUpload,
    'hl-Button': Button,
    'hl-Input': Input,
    'hl-Select': Select,
    'hl-RadioButtonTapWithIcon': RadioButtonTapWithIcon,
  },

  props: {
    landlordId: [String, Number],
    copyFromRentalObjectId: [String, Number],
  },

  data() {
    return {
      categoryMap,
      isCopyMode: false,
      otherValue: 'other',
      rentalObject: {
        type: '',
        postal_area: '',
        postal_zipcode: '',
        street: '',
        apartmentNumber: '',
        alias: '',
        floor: null,
      },
      postalAreaCancelSource: undefined,
    };
  },

  computed: {
    ...mapGetters(['getLandlordProfileMeta', 'getRentalObject']),

    landlord() {
      return this.getLandlordProfileMeta(this.landlordId);
    },

    isLandlordProfessional() {
      if (this.landlord == null || this.landlord.category == null) return;
      return this.landlord.category === 'professional';
    },

    propertyTypeOptions() {
      return [
        { name: 'Rom i bofellesskap', value: 'co-op' },
        { name: 'Tomannsbolig', value: 'semi-detached' },
        { name: 'Rekkehus', value: 'chained-house' },
        { name: 'Næringseiendom', value: 'commercial' },
        { name: 'Garasje / Parkering', value: 'garage-parking' },
      ];
    },

    showOtherCategories() {
      if (this.rentalObject.type == null) return;
      const otherCategories = ['co-op', 'semi-detached', 'chained-house', 'commercial', 'garage-parking'];
      return otherCategories.includes(this.rentalObject.type) || this.rentalObject.type === 'other';
    },

    showRoomNumber() {
      return this.rentalObject.type === 'dorm' || this.rentalObject.type === 'co-op';
    },

    showFloor() {
      return (
        this.rentalObject.type === 'apartment' ||
        this.rentalObject.type === 'dorm' ||
        this.rentalObject.type === 'co-op' ||
        this.rentalObject.type === 'semi-detached'
      );
    },
  },

  methods: {
    ...mapActions(['loadLandlordProfiles', 'fetchData']),

    async fetchPostalArea(postalCode) {
      if (this.postalAreaCancelSource) {
        this.postalAreaCancelSource.cancel();
        this.postalAreaCancelSource = undefined;
      }

      this.rentalObject.postal_area = '';

      if (postalCode.length !== 4) {
        return;
      }

      try {
        this.postalAreaCancelSource = axios.CancelToken.source();
        const res = await postalCodeLookup(postalCode, this.postalAreaCancelSource.token);

        if (res.error) {
          this.$refs.postalZipcode.setErrors(['Postnummer ikke funnet']);
        } else {
          this.rentalObject.postal_area = res.poststed;
        }
      } catch (error) {
        if (axios.isCancel(error)) return;

        this.$toast('Kunne ikke hente poststed. Sjekk at postnummeret har gyldig format.', 'error');
      }
    },

    updateFloor() {
      if ('apartmentNumber' in this.rentalObject) {
        // Extract the floor number from the apartmentNumber.
        const m = this.rentalObject.apartmentNumber.match(/^[a-zA-Z](\d\d)\d\d$/);
        if (m && m.length > 1) {
          const floor = new String(parseInt(m[1]));
          this.rentalObject.floor = floor;
          this.$nextTick(() => {
            this.$validator.validateAll();
          });
        }
      }
    },

    goBack() {
      this.$router.back();
    },

    async onSubmit() {
      const success = await this.$refs.newRentalObjectForm.validate();

      if (!success) {
        this.$nextTick(() => {
          const el = document.querySelector('[class*=is-invalid]');
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        });
        return;
      }

      const rentalObjectId = await this.createRentalObject();
      await this.loadLandlordProfiles();
      await this.fetchData({
        tenancies: ['active', 'free', 'resigned'],
      });

      if (rentalObjectId) {
        this.$router.push({
          name: 'rentalObject_tenancies',
          params: {
            landlordId: this.landlordId,
            rentalObjectId,
          },
        });
      }
    },

    async createRentalObject() {
      if (this.landlordId == null) throw new Error("Can't create rental object. Missing landlordId");
      try {
        const api = new RentalObjectApi();
        const rentalObjectCopy = JSON.parse(JSON.stringify(this.rentalObject));
        /* rentalObjectCopy.category = this.categoryMap[rentalObjectCopy.category]; */
        const res = await api.postLandlordRentalObject(this.landlordId, {
          body: rentalObjectCopy,
        });
        this.$toast('Leieobjektet er opprettet', 'success');
        // we redirect to the rental object tenancies page with the new rOId
        if ('id' in res) {
          return res.id;
        }
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke lagre leieobjektet', 'error');
      }
      return undefined;
    },

    prefillRentalObject(data) {
      // We clean the default config
      this.rentalObject = {};
      // We map the fields into the form
      this.rentalObject = {
        type: data.details.type ? data.details.type : '',
        postal_area: data.address.postalArea ? data.address.postalArea : '',
        postal_zipcode: data.address.postalZipcode ? data.address.postalZipcode : '',
        street: data.address.street ? data.address.street : '',
        alias: data.details.alias ? data.details.alias : '',
        apartmentNumber: data.details.apartmentNumber ? data.details.apartmentNumber : '',
      };

      if (this.showFloor) {
        this.rentalObject.floor = data.details.floor ? data.details.floor : '';
      }
      if (this.showRoomNumber) {
        this.rentalObject.roomNumber = data.details.roomNumber ? data.details.roomNumber : '';
      }
    },
  },

  async mounted() {
    if (this.copyFromRentalObjectId) {
      this.isCopyMode = true;
      try {
        await this.$store.dispatch('fetchRentalObject', {
          tenancies: ['active', 'free'],
          rentalObjectId: this.copyFromRentalObjectId,
        });
        await this.prefillRentalObject(this.getRentalObject(this.copyFromRentalObjectId));
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke hente leieobjektet', 'error');
      }
    } else {
      this.rentalObject.type = 'apartment';
    }
  },
};
</script>
